'use client'

import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'

import { loadTurnstileScript } from '@/lib/turnstileScript'

declare global {
  interface Window {
    onTurnstileLoad?: () => void
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          action?: string
          theme?: 'light' | 'dark' | 'auto'
          size?: 'normal' | 'flexible' | 'compact'
          callback?: (token: string) => void
          'error-callback'?: (errorCode: string) => void
          'expired-callback'?: () => void
        },
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
      ready: (callback: () => void) => void
    }
  }
}

const TURNSTILE_MIN_WIDTH = 300
const TURNSTILE_HEIGHT = 70

export type TurnstileWidgetHandle = {
  reset: () => void
}

export type TurnstileWidgetProps = {
  action: string
  onVerify: (token: string) => void
  onExpire?: () => void
  onError?: (errorCode: string) => void
  className?: string
  /** Set false when parent UI (modal, drawer, tab) is hidden. Defaults to true. */
  active?: boolean
}

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget(
    { action, onVerify, onExpire, onError, className, active = true },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null)
    const outerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)
    const renderedActionRef = useRef<string | null>(null)
    const renderedScaledRef = useRef<boolean | null>(null)
    const onVerifyRef = useRef(onVerify)
    const onExpireRef = useRef(onExpire)
    const onErrorRef = useRef(onError)
    const [scriptReady, setScriptReady] = useState(false)
    const [isWidgetReady, setIsWidgetReady] = useState(false)
    const [scale, setScale] = useState(1)
    const [isMeasured, setIsMeasured] = useState(false)

    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
    const showSkeleton = active && !isWidgetReady

    useEffect(() => {
      onVerifyRef.current = onVerify
      onExpireRef.current = onExpire
      onErrorRef.current = onError
    }, [onVerify, onExpire, onError])

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current)
        }
      },
    }))

    useLayoutEffect(() => {
      const element = outerRef.current
      if (!element) return

      const updateScale = (width: number) => {
        setScale(width < TURNSTILE_MIN_WIDTH ? width / TURNSTILE_MIN_WIDTH : 1)
        setIsMeasured(true)
      }

      updateScale(element.getBoundingClientRect().width)

      const observer = new ResizeObserver(([entry]) => {
        updateScale(entry.contentRect.width)
      })
      observer.observe(element)

      return () => observer.disconnect()
    }, [])

    useEffect(() => {
      if (!active) {
        return
      }

      let cancelled = false

      loadTurnstileScript()
        .then(() => {
          if (!cancelled) setScriptReady(true)
        })
        .catch((error) => {
          console.error(error)
          onErrorRef.current?.('script-load-failed')
        })

      return () => {
        cancelled = true
      }
    }, [active])

    useEffect(() => {
      if (!active) {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current)
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
          renderedActionRef.current = null
          renderedScaledRef.current = null
        }
        setIsWidgetReady(false)
        return
      }

      if (!scriptReady || !siteKey || !containerRef.current || !window.turnstile || !isMeasured) {
        return
      }

      const isScaled = scale < 1

      if (
        widgetIdRef.current &&
        renderedActionRef.current === action &&
        renderedScaledRef.current === isScaled
      ) {
        return
      }

      if (widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
        renderedActionRef.current = null
        renderedScaledRef.current = null
        setIsWidgetReady(false)
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme: 'auto',
        size: 'flexible',
        callback: (token) => {
          onVerifyRef.current(token)
        },
        'error-callback': (errorCode) => {
          onErrorRef.current?.(errorCode)
        },
        'expired-callback': () => {
          onExpireRef.current?.()
        },
      })
      renderedActionRef.current = action
      renderedScaledRef.current = isScaled
      setIsWidgetReady(true)

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
          renderedActionRef.current = null
          renderedScaledRef.current = null
        }
        setIsWidgetReady(false)
      }
    }, [active, scriptReady, siteKey, action, isMeasured, scale])

    if (!siteKey) return null

    const isScaled = scale < 1
    const visibleHeight = isScaled ? TURNSTILE_HEIGHT * scale : TURNSTILE_HEIGHT

    return (
      <div
        ref={outerRef}
        className={`relative w-full max-w-full min-w-0 overflow-hidden transition-all duration-300 ${className ?? ''}`}
        style={{ minHeight: visibleHeight }}
        aria-busy={showSkeleton}
        aria-label={showSkeleton ? 'Loading security verification' : undefined}
        aria-hidden={!active}
      >
        {showSkeleton && (
          <div
            className="absolute inset-0 flex items-center gap-3 rounded border border-neutral-300 bg-neutral-100 px-3 dark:border-neutral-600 dark:bg-neutral-800"
            aria-hidden="true"
          >
            <div className="h-5 w-5 shrink-0 animate-pulse rounded-sm border border-neutral-300 bg-neutral-200 dark:border-neutral-500 dark:bg-neutral-700" />
            <div className="h-4 max-w-[180px] flex-1 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
          </div>
        )}
        <div
          className="relative origin-left overflow-hidden"
          style={
            isScaled
              ? {
                  width: TURNSTILE_MIN_WIDTH,
                  height: TURNSTILE_HEIGHT,
                  transform: `scale(${scale})`,
                }
              : { width: '100%' }
          }
        >
          <div ref={containerRef} className="w-full max-w-full" />
          {isWidgetReady && (
            <div
              className="absolute bottom-0 left-[2.75rem] right-[4.5rem] top-0 z-10 cursor-default"
              aria-hidden="true"
              data-turnstile-label-shield
              onPointerDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
            />
          )}
        </div>
      </div>
    )
  },
)
